<template>
  <!-- ===== BOOKINGS OVERVIEW ===== -->
  <div class="dashboard-component p-4" :class="{ 'reservation-overview--loading': loading }">
    <h3 class="mb-3">Boekingen - Maandoverzicht</h3>

    <!-- Filters -->
    <div class="row g-3 align-items-center mb-4">
      <div class="col-auto">
        <select v-model="selectedHouseId" class="form-select">
          <option value="all">Alle huizen</option>
          <option v-for="house in houses" :key="house.id" :value="house.id">{{ house.title }}</option>
        </select>
      </div>
      <div class="col-auto">
        <select v-model="selectedMonth" class="form-select">
          <option v-for="(name, index) in monthNames" :key="index" :value="index + 1">{{ name }}</option>
        </select>
      </div>
      <div class="col-auto">
        <select v-model="selectedYear" class="form-select">
          <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <div v-if="displayMode === 'text'" class="col-auto">
        <label class="cancelled-filter">
          <input v-model="includeCancelled" class="cancelled-filter__input" type="checkbox">
          <span class="cancelled-filter__toggle" aria-hidden="true">
            <span class="cancelled-filter__knob"></span>
          </span>
          <span>Geannuleerde tonen</span>
        </label>
      </div>
      <div class="col-auto ms-auto">
        <div class="view-switch" role="group" aria-label="Weergavemodus">
          <button type="button" class="view-switch__button" :class="{ active: displayMode === 'visual' }" aria-label="Visuele weergave" title="Visuele weergave" @click="displayMode = 'visual'">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
          </button>
          <button type="button" class="view-switch__button" :class="{ active: displayMode === 'text' }" aria-label="Tekstweergave" title="Tekstweergave" @click="displayMode = 'text'">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h14M7 12h14M7 19h14"/><circle cx="3" cy="5" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="19" r="1"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Houses overview  -->
    <div class="component-load" v-if="loading">
      Bezig met laden...
    </div>

    <div class="reservation-total mb-4" aria-live="polite">
      <span>Totaal {{ monthNames[selectedMonth - 1].toLowerCase() }} {{ selectedYear }}</span>
      <strong>{{ reservationsForDisplayedHouses.length }} {{ reservationLabel(reservationsForDisplayedHouses.length) }}</strong>
    </div>

    <template v-if="displayMode === 'visual'">
    <div v-for="house in displayedHouses" :key="house.id" class="mb-5">
      <h5 class="mb-2">{{ house.title }}</h5>

      <div class="d-flex flex-wrap gap-1">
        <div
            v-for="day in daysInMonth"
            :key="day"
            class="d-flex align-items-center justify-content-center text-white"
            :class="[dayColor(house, day), { 'reservation-day': getReservations(house, day).length, 'reservation-day--highlighted': !isChangeover(house, day) && isHighlighted(house, day), 'reservation-day--muted': hasHoveredReservation && !isHighlighted(house, day) }]"
            style="width: 34px; height: 34px;"
            @click="!isChangeover(house, day) && openReservation(house, day)"
            @mousemove="!isChangeover(house, day) && hoverReservation(getReservation(house, day), house.id, getBooking(house, day).start, $event)"
            @mouseleave="clearHoveredReservation"
            :data-available="getBooking(house, day) ? getBooking(house, day).available : null"
            :data-has-arrival="getBooking(house, day) ? getBooking(house, day).hasArrival : null"
            :data-has-departure="getBooking(house, day) ? getBooking(house, day).hasDeparture : null"
            :data-is-not-booked="getBooking(house, day) ? getBooking(house, day).isNotBooked : null"
        >
          <template v-if="isChangeover(house, day)">
            <svg class="reservation-day__split" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" @mouseleave="clearHoveredReservation">
              <polygon
                  points="0,0 57,0 41,100 0,100"
                  fill="#f5a623"
                  :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && !isReservationHighlighted(getDepartureReservation(house, day), house.id) }"
              />
              <polygon
                  points="59,0 100,0 100,100 43,100"
                  fill="#f5a623"
                  :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && !isReservationHighlighted(getArrivalReservation(house, day), house.id) }"
              />
              <polygon
                  class="reservation-day__split-hitarea"
                  points="0,0 58,0 42,100 0,100"
                  @click.stop="openReservationById(getDepartureReservation(house, day).id)"
                  @mousemove.stop="hoverReservation(getDepartureReservation(house, day), house.id, getBooking(house, day).start, $event)"
              />
              <polygon
                  class="reservation-day__split-hitarea"
                  points="58,0 100,0 100,100 42,100"
                  @click.stop="openReservationById(getArrivalReservation(house, day).id)"
                  @mousemove.stop="hoverReservation(getArrivalReservation(house, day), house.id, getBooking(house, day).start, $event)"
              />
            </svg>
            <span class="reservation-day__number">{{ day }}</span>
          </template>
          <template v-else-if="isTransition(house, day)">
            <svg class="reservation-day__split" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <polygon
                  points="0,0 57,0 41,100 0,100"
                  :fill="isDeparture(house, day) ? '#f5a623' : '#28a745'"
                  :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && isHighlighted(house, day) && !isDeparture(house, day) }"
              />
              <polygon
                  points="59,0 100,0 100,100 43,100"
                  :fill="isArrival(house, day) ? '#f5a623' : '#28a745'"
                  :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && isHighlighted(house, day) && !isArrival(house, day) }"
              />
            </svg>
            <span class="reservation-day__number">{{ day }}</span>
          </template>
          <template v-else>{{ day }}</template>
          <span v-if="hoveredReservation && hoveredHouseId === house.id && hoveredDay === getBooking(house, day).start" class="reservation-tooltip">
            {{ reservationTooltip(hoveredReservation) }}
          </span>
        </div>
      </div>
    </div>
    </template>

    <div v-else>
      <h5 class="reservation-list__heading">
        {{ monthNames[selectedMonth - 1] }} · {{ selectedHouseId === 'all' ? 'Alle huizen' : displayedHouses[0].title }}
      </h5>
      <ul v-if="reservationsForDisplayedHouses.length" class="reservation-list mb-0">
        <li v-for="(reservation, index) in reservationsForDisplayedHouses" :key="reservation.dedupeKey">
          <button type="button" class="reservation-list__item" @click="openReservationById(reservation.id)">
            {{ index + 1 }}. -> {{ formatDate(reservation.arrivalDate) }} t/m {{ formatDate(reservation.departureDate) }} - {{ reservation.houseTitle }} - {{ reservation.fullName || '-' }} ({{ reservation.agencyName || '-' }}) - {{ formatDate(reservation.addedDate) }} <strong v-if="reservation.cancelled" class="reservation-list__cancelled">Geannuleerd</strong>
          </button>
        </li>
      </ul>
      <p v-else class="text-muted mb-0">Geen reserveringen.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "ReservationMonthOverview",
  data() {
    const today = new Date();

    const origin = window.location.origin.replace("admin.", "");
    const apiBase = `${origin}/ajax`;

    return {
      apiBase,
      selectedMonth: today.getMonth() + 1,
      selectedYear: today.getFullYear(),
      selectedHouseId: 'all',
      displayMode: 'visual',
      includeCancelled: false,
      years: Array.from({ length: 5 }, (_, i) => today.getFullYear() - 1 + i),
      monthNames: [
        "Januari", "Februari", "Maart", "April", "Mei", "Juni",
        "Juli", "Augustus", "September", "Oktober", "November", "December",
      ],
      houses: [],
      loading: false,
      hoveredReservationId: null,
      hoveredReservation: null,
      hoveredHouseId: null,
      hoveredDay: null,
    };
  },
  computed: {
    daysInMonth() {
      return new Date(this.selectedYear, this.selectedMonth, 0).getDate();
    },
    hasHoveredReservation() {
      return Boolean(this.hoveredReservationId);
    },
    displayedHouses() {
      return this.selectedHouseId === 'all'
        ? this.houses
        : this.houses.filter(house => house.id === this.selectedHouseId);
    },
    reservationsForDisplayedHouses() {
      const reservations = new Map();

      this.displayedHouses.forEach(house => {
        house.bookings.forEach(booking => {
          const reservationDetails = this.includeCancelled
            ? [...(booking.reservationDetails || []), ...(booking.cancelledReservationDetails || [])]
            : (booking.reservationDetails || []);

          reservationDetails.forEach((reservation, index) => {
            const id = reservation.id || booking.reservations[index];
            const dedupeKey = `${house.id}|${id}`;

            if (id && this.arrivesInSelectedMonth(reservation.arrivalDate) && !reservations.has(dedupeKey)) {
              reservations.set(dedupeKey, Object.assign({}, reservation, { id, dedupeKey, houseTitle: house.title }));
            }
          });
        });
      });

      return this.sortReservationsByArrival(Array.from(reservations.values()));
    },
  },
  watch: {
    selectedMonth() {
      this.filterData();
    },
    selectedYear() {
      this.filterData();
    },
  },
  created() {
    this.loadHouses();
  },
  methods: {
    async loadHouses() {
      try {
        this.clearHoveredReservation();
        this.loading = true;
        const res = await fetch(`${this.apiBase}/house/`);
        const data = await res.json();

        this.houses = data
            //.filter((_, index) => index === 1) //debug purpose
            .map(h => ({
              id: h.hashId,
              title: h.title,
              bookings: [],
            }));

        await this.loadAvailabilities();
      } catch (err) {
        console.error("Failed to load houses:", err);
      } finally {
        this.loading = false;
      }
    },

    async filterData() {
      this.clearHoveredReservation();
      this.loading = true;
      await this.$nextTick();

      try {
        if (!this.houses.length) {
          await this.loadHouses();
        } else {
          await this.loadAvailabilities();
        }
      } finally {
        this.loading = false;
      }
    },

    async loadAvailabilities() {
      if (!this.houses.length) return;

      const month = this.selectedMonth;
      const year = this.selectedYear;

      const promises = this.houses.map(async (house) => {
        try {
          house.bookings = [];

          const url = `${this.apiBase}/availability/${house.id}/${month}/${year}`;
          const res = await fetch(url);
          const data = await res.json();

          if (!data || !data.calendar || !data.calendar.length) return;

          const days = data.calendar[0].days.flat().filter(d => d && d.date);

          house.bookings = days.map(d => ({
            start: `${d.date.split('-')[0]}-${String(d.date.split('-')[1]).padStart(2,'0')}-${String(d.date.split('-')[2]).padStart(2,'0')}`,
            end: `${d.date.split('-')[0]}-${String(d.date.split('-')[1]).padStart(2,'0')}-${String(d.date.split('-')[2]).padStart(2,'0')}`,
            available: d.available,
            hasArrival: d.hasArrival,
            hasDeparture: d.hasDeparture,
            isNotBooked: d.isNotBooked,
            reservations: d.reservations,
            reservationDetails: d.reservationDetails || [],
            cancelledReservationDetails: d.cancelledReservationDetails || [],
          }));
        } catch (err) {
          console.error(`Error loading availability for ${house.title}`, err);
        }
      });

      await Promise.all(promises);
    },

    getBooking(house, day) {
      const dateStr = `${this.selectedYear}-${String(this.selectedMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      return house.bookings.find(b => b.start === dateStr);
    },

    dayColor(house, day) {
      const booking = this.getBooking(house, day);

      if (!booking) return "bg-loading"; //Added this state to clearly indicate when something goes wrong and no dates are being found
      if (booking.hasArrival && booking.hasDeparture) return "reservation-day--changeover";
      if (booking.hasArrival) return "reservation-day--arrival-only";
      if (booking.hasDeparture) return "reservation-day--departure-only";
      if (!booking.available && !booking.isNotBooked) return "bg-danger";
      return "bg-success";
    },

    getReservation(house, day) {
      return this.getReservations(house, day)[0] || null;
    },

    getReservations(house, day) {
      const booking = this.getBooking(house, day);
      if (!booking) return [];

      return booking.reservationDetails.map((reservation, index) => {
        const id = reservation.id || booking.reservations[index];
        return id ? Object.assign({}, reservation, { id }) : null;
      }).filter(Boolean);
    },

    reservationsForHouse(house) {
      const reservations = new Map();

      house.bookings.forEach(booking => {
        (booking.reservationDetails || []).forEach((reservation, index) => {
          const id = reservation.id || booking.reservations[index];
          if (id && !reservations.has(id)) reservations.set(id, Object.assign({}, reservation, { id }));
        });
      });

      return this.sortReservationsByArrival(Array.from(reservations.values()));
    },

    sortReservationsByArrival(reservations) {
      return reservations.sort((a, b) => {
        const arrivalComparison = this.dateSortValue(a.arrivalDate) - this.dateSortValue(b.arrivalDate);
        return arrivalComparison || this.dateSortValue(a.departureDate) - this.dateSortValue(b.departureDate);
      });
    },

    dateSortValue(date) {
      const [year, month, day] = String(date || '').slice(0, 10).split('-').map(Number);
      return year && month && day ? new Date(year, month - 1, day).getTime() : Number.MAX_SAFE_INTEGER;
    },

    arrivesInSelectedMonth(date) {
      const [year, month] = String(date || '').slice(0, 10).split('-').map(Number);
      return year === this.selectedYear && month === this.selectedMonth;
    },

    formatDate(date) {
      if (!date) return '-';
      const [year, month, day] = String(date).slice(0, 10).split('-');
      return year && month && day ? `${day}-${month}-${year}` : date;
    },

    reservationLabel(total) {
      return total === 1 ? 'reservatie' : 'reservaties';
    },

    getArrivalReservation(house, day) {
      return this.getReservations(house, day).find(reservation => reservation.isArrival) || this.getReservations(house, day)[0];
    },

    getDepartureReservation(house, day) {
      const reservations = this.getReservations(house, day);
      return reservations.find(reservation => reservation.isDeparture) || reservations[1] || reservations[0];
    },

    isChangeover(house, day) {
      const booking = this.getBooking(house, day);
      return booking && booking.hasArrival && booking.hasDeparture && this.getReservations(house, day).length > 1;
    },

    isTransition(house, day) {
      const booking = this.getBooking(house, day);
      return booking && (booking.hasArrival || booking.hasDeparture);
    },

    isArrival(house, day) {
      const booking = this.getBooking(house, day);
      return booking && booking.hasArrival;
    },

    isDeparture(house, day) {
      const booking = this.getBooking(house, day);
      return booking && booking.hasDeparture;
    },

    hoverReservation(reservation, houseId, date, event) {
      this.hoveredReservationId = reservation ? reservation.id : null;
      this.hoveredReservation = reservation || null;
      this.hoveredHouseId = reservation ? houseId : null;
      this.hoveredDay = reservation ? date : null;
    },

    clearHoveredReservation() {
      this.hoveredReservationId = null;
      this.hoveredReservation = null;
      this.hoveredHouseId = null;
      this.hoveredDay = null;
    },

    isHighlighted(house, day) {
      return house.id === this.hoveredHouseId && this.getReservations(house, day).some(reservation => reservation.id === this.hoveredReservationId);
    },

    isReservationHighlighted(reservation, houseId) {
      return reservation && houseId === this.hoveredHouseId && reservation.id === this.hoveredReservationId;
    },

    reservationTooltip(reservation) {
      return [reservation.code, reservation.fullName].filter(Boolean).join(' — ');
    },

    openReservation(house, day) {
      const booking = this.getBooking(house, day);

      if(booking && booking.reservations[0]){
        this.openReservationById(booking.reservations[0]);
      }
    },

    openReservationById(reservationId) {
      if (reservationId) {
        this.$router.push({ name: "dashboard-form", params: {
            bundle: "reservation",
            definition: "form",
            objectId: reservationId
          } });
      }
    }

  },
};
</script>

<style scoped>
.reservation-day { cursor: pointer; position: relative; transition: filter .22s ease, opacity .22s ease, transform .22s ease; }
.reservation-day--highlighted { box-shadow: inset 0 0 0 3px rgba(33, 37, 41, .9), 0 2px 5px rgba(33, 37, 41, .35); filter: brightness(1.24) saturate(1.15); transform: scale(1.08); z-index: 1; }
.reservation-day--muted { filter: grayscale(.45) opacity(.45) blur(1px); }
.reservation-day--changeover, .reservation-day--arrival-only, .reservation-day--departure-only { background: #fff !important; position: relative; }
.reservation-day__split { bottom: 0; height: 100%; left: 0; position: absolute; top: 0; width: 100%; z-index: 1; }
.reservation-day__split polygon { cursor: pointer; transition: opacity .22s ease; }
.reservation-day__split-hitarea { fill: rgba(0, 0, 0, .001); }
.reservation-day__split-half--faded { opacity: .45; }
.reservation-day__number { pointer-events: none; position: relative; z-index: 3; }
.reservation-overview--loading > :not(.component-load) { filter: blur(3px); pointer-events: none; }
.reservation-tooltip { background: #212529; border-radius: 4px; bottom: calc(100% + 7px); color: #fff; font-size: 12px; left: 50%; padding: 5px 8px; pointer-events: none; position: absolute; transform: translateX(-50%); white-space: nowrap; z-index: 2; }
.reservation-tooltip::after { border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid #212529; content: ''; left: 50%; position: absolute; top: 100%; transform: translateX(-50%); }
.reservation-list { list-style: none; padding-left: 0; }
.reservation-list__heading { background: rgba(108, 117, 125, .08); border: 1px solid rgba(108, 117, 125, .55); border-radius: 4px; font-size: 1.05rem; font-weight: 600; margin: 0 0 .75rem; padding: .65rem .85rem; }
.reservation-list__item { background: none; border: 0; color: inherit; padding: .25rem 0; text-align: left; }
.reservation-list__item:hover { color: #0d6efd; text-decoration: underline; }
.reservation-list__cancelled { color: #dc3545; margin-left: .4rem; }
.cancelled-filter { align-items: center; cursor: pointer; display: inline-flex; gap: .5rem; white-space: nowrap; }
.cancelled-filter__input { height: 1px; opacity: 0; overflow: hidden; position: absolute; width: 1px; }
.cancelled-filter__toggle { background: #e9ecef; border-radius: 999px; box-sizing: border-box; display: inline-flex; height: 34px; padding: 3px; transition: background-color .2s ease, box-shadow .2s ease; width: 58px; }
.cancelled-filter__knob { background: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(0, 0, 0, .2); display: block; height: 28px; transition: transform .2s ease; width: 28px; }
.cancelled-filter__input:checked + .cancelled-filter__toggle { background: var(--color-1); }
.cancelled-filter__input:checked + .cancelled-filter__toggle .cancelled-filter__knob { transform: translateX(24px); }
.cancelled-filter__input:focus-visible + .cancelled-filter__toggle { box-shadow: 0 0 0 3px rgba(var(--color-1-rgb), .25); }
.reservation-total { align-items: center; background: rgba(108, 117, 125, .08); border: 1px solid rgba(108, 117, 125, .3); border-radius: 6px; display: flex; gap: .75rem; justify-content: space-between; padding: .65rem .85rem; }
.reservation-total strong { color: var(--color-1); }
.view-switch { background: #e9ecef; border-radius: 999px; display: inline-flex; gap: 2px; padding: 3px; }
.view-switch__button { align-items: center; background: transparent; border: 0; border-radius: 999px; color: #6c757d; display: flex; height: 34px; justify-content: center; padding: 0; width: 38px; }
.view-switch__button.active { background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, .2); color: #212529; }
.view-switch__button svg { fill: none; height: 18px; stroke: currentColor; stroke-linecap: round; stroke-width: 2; width: 18px; }
.view-switch__button:first-child svg rect { fill: currentColor; stroke: none; }
</style>
